import '@fontsource/open-sans/400.css'
import '@fontsource/open-sans/600.css'

import { mode } from '@chakra-ui/theme-tools'
import { extendTheme } from '@chakra-ui/react'

export enum LayerStyles {
  SMALL_PILL = 'small-pill',
  SMALL_PILL_VERTICAL = 'small-pill-vertical',
  SMALL_PILL_IMAGE = 'small-pill-image',
  SMALL_PILL_DEFAULT = 'small-pill-default',
  LARGE_PILL = 'large-pill',
  EXTRA_LARGE_PILL = 'extra-large-pill',
  MAIN_PILL = 'main-pill',
  BTN_INPUT_PILL = 'input-btn-pill',
  INPUT = 'input',
  INPUT_GROUP = 'input-group',
}

export const defaultTheme = extendTheme({
  colors: {
    brand: '#6bce4c',
    white: '#fff',
    black: '#012737',
    red: '#c9302c',
    darkRed: '#ac2925',
    darkBlack: '#343a40',
    blackAlpha: {
      50: 'rgba(51, 51, 51, 0.04)',
      100: 'rgba(51, 51, 51, 0.06)',
      200: 'rgba(51, 51, 51, 0.08)',
      300: 'rgba(51, 51, 51, 0.16)',
      400: 'rgba(51, 51, 51, 0.24)',
      500: 'rgba(51, 51, 51, 0.36)',
      600: 'rgba(1,  39, 55, 0.80)', //Mostly used for AlertDialogOverlay
      700: 'rgba(51, 51, 51, 0.64)',
      800: 'rgba(51, 51, 51, 0.80)',
      900: 'rgba(51, 51, 51, 0.92)',
    },
    whiteAlpha: {
      50: 'rgba(255, 255, 255, 0.04)',
      100: 'rgba(255, 255, 255, 0.06)',
      200: 'rgba(255, 255, 255, 0.08)',
      300: 'rgba(255, 255, 255, 0.16)',
      400: 'rgba(255, 255, 255, 0.24)',
      500: 'rgba(255, 255, 255, 0.36)',
      600: 'rgba(255, 255, 255, 0.48)',
      700: 'rgba(255, 255, 255, 0.64)',
      800: 'rgba(255, 255, 255, 0.80)',
      900: 'rgba(255, 255, 255, 0.92)',
    },
    // workaround for changing color of the progress bar as mentioned here - https://github.com/chakra-ui/chakra-ui/issues/569

    green: { 200: '#6bce4c', 500: '#6bce4c', 600: '#19c90c' },
    lightGreen: '#f1ffe2',
    teal: '#63bcbf',
    gray: '#d6e2e8',
    grey: '#d6e2e8',
    darkGrey: '#6b7980',
    ashGrey: '#A9A9A9',
    purple: '#6e57ff',
    purpleAlpha: 'rgba(222,217,255,.5)',
    error: '#f0946a',
  },

  components: {
    Button: {
      baseStyle: {
        borderRadius: 'full',
        fontWeight: 400,
        padding: '10px 16px',
        '&:focus': {
          boxShadow: 'none',
        },
      },
      defaultProps: {
        variant: 'outline',
      },
      variants: {
        outline: {
          border: '2px solid',
          borderColor: 'white',
        },
      },
    },
    Checkbox: {
      baseStyle: {
        border: '2px solid red',
        fontWeight: 400,
        fontSize: '15px',
      },
    },
    Heading: {
      baseStyle: {
        fontWeight: 400,
        textAlign: 'center',
      },
    },
    // Select is styled withing a component
    FluidImage: {
      baseStyle: {
        height: '60px',
      },
    },
    Radio: {
      baseStyle: {
        control: {
          border: '1px solid',
          borderColor: 'darkGrey',
          marginRight: 4,
          fontSize: '10pt',
        },
        label: {
          fontSize: 'xs',
        },
      },
    },
  },
  fonts: {
    heading: `'Open Sans', sans-serif`,
    body: `'Open Sans', sans-serif`,
  },
  fontSizes: {
    xxs: '.82rem',
    xs: '.89rem',
    sm: '1.4rem',
    md: '1.75rem',
    lg: '1rem',
    xl: '1.4rem',
    '2xl': '1.58rem',
    '3xl': '1.8rem',
    '4xl': '2rem',
  },
  lineHeights: {
    2: '0.5rem',
    3: '.75rem',
    4: '1rem',
    5: '1.25rem',
    6: '1.5rem',
    7: '1.75rem',
    8: '2rem',
    9: '2.25rem',
    10: '2.5rem',
    11: '2.75rem',
    12: '3rem',
    13: '3.25rem',
    14: '3.5rem',
    15: '3.75rem',
    16: '4rem',
  },
  radii: {
    default: '16px',
    medium: '8px',
    small: '4px',
  },
  space: {
    1: '2px',
    2: '5px',
    3: '0',
    4: '16px',
    10: '4vw',
  },
  layerStyles: {
    [LayerStyles.SMALL_PILL]: {
      cursor: 'pointer',
      border: '1px solid',
      borderColor: 'brand',
      borderRadius: '2xl',
      fontSize: 'xs',
      color: 'white',
      fontWeight: 700,
      backgroundImage: 'linear-gradient(90deg, #6bce4c 10%, #4caf50 100%)',
      paddingX: '18px',
      paddingY: '8px',
      marginX: 0.5,
      marginY: 1,
      transition: 'all 0.3s',
      _hover: {
        opacity: 0.85,
      },
      _checked: {
        bg: 'green.500',
        color: 'white',
      },
      _focus: {
        borderColor: 'brand',
        boxShadow: '0 0 0 2px #5bce4c',
        transform: 'scale(1.05)',
      },
    },
    [LayerStyles.SMALL_PILL_DEFAULT]: {
      cursor: 'pointer',
      border: '2px solid',
      borderColor: 'black',
      borderRadius: '2xl',
      fontSize: 'xs',
      color: 'black',
      fontWeight: 700,
      // backgroundImage: 'linear-gradient(90deg, #6bce4c 10%, #4caf50 100%)',
      bg: 'grey',
      paddingX: '18px',
      paddingY: '8px',
      marginX: 0.5,
      marginY: 1,
      transition: 'all 0.3s',
      _hover: {
        opacity: 0.85,
      },
      _checked: {
        bg: 'green.500',
        color: 'white',
        borderColor: 'brand',
      },
      _focus: {
        borderColor: 'brand',
        boxShadow: '0 0 0 2px #5bce4c',
        transform: 'scale(1.05)',
      },
    },
    [LayerStyles.SMALL_PILL_VERTICAL]: {
      cursor: 'pointer',
      border: '2px',
      borderColor: 'green.200',
      borderRadius: 'xl',
      height: '5vh',
      width: '25vw',
      maxWidth: '200px',
      bg: 'lightGreen',
      color: 'green.200',
      fontSize: 'xs',
      fontWeight: 700,
      textAlign: 'center',
      margin: '10px auto 0',
      _checked: {
        bg: 'green.200',
        color: 'white',
      },
      _focus: {
        borderColor: 'brand',
        boxShadow: '0 0 0 2px #5bce4c',
      },
    },
    [LayerStyles.LARGE_PILL]: {
      border: '1px',
      borderColor: 'grey',
      height: '7.7vh',
      maxHeight: '70px',
      width: '37vh',
      maxWidth: '298px',
      padding: 2,
      marginBottom: 2,
      fontSize: 'xs',
      fontWeight: 'bold',
      borderRadius: 'full',
      whiteSpace: 'normal',
      margin: '15px auto 0',
      _hover: {
        border: '2px',
        borderColor: 'brand',
      },
      _active: {
        border: '2px',
        borderColor: 'brand',
      },
      _focus: {
        borderColor: 'brand',
        boxShadow: '0 0 0 2px #5bce4c',
      },
    },
    [LayerStyles.EXTRA_LARGE_PILL]: {
      border: '1px',
      borderColor: 'grey',
      height: '7.7vh',
      maxHeight: '70px',
      width: '50vh',
      maxWidth: '450px',
      padding: 6,
      marginBottom: 2,
      fontSize: 'xs',
      fontWeight: 'bold',
      borderRadius: 'full',
      whiteSpace: 'normal',
      margin: '15px auto 0',
      _hover: {
        border: '2px',
        borderColor: 'brand',
      },
      _active: {
        border: '2px',
        borderColor: 'brand',
      },
      _focus: {
        borderColor: 'brand',
        boxShadow: '0 0 0 2px #5bce4c',
      },
    },
    [LayerStyles.MAIN_PILL]: {
      cursor: 'pointer',
      backgroundColor: 'green.500',
      borderRadius: '2xl',
      color: 'white',
      fontSize: 'xs',
      fontWeight: 700,
      paddingX: '16px',
      paddingY: '8px',
      marginX: 0.5,
      marginY: 1,
    },
    _focus: {
      borderColor: 'brand',
      boxShadow: '0 0 0 2px #5bce4c',
    },
    [LayerStyles.BTN_INPUT_PILL]: {
      position: 'absolute',
      right: 2,
      top: 2,
      bottom: 2,
      width: 20,
      height: 'auto',
      size: '2xl',
      borderRadius: 'md',
      border: '2px solid transparent',
      backgroundColor: 'green.500',
      _hover: {
        backgroundColor: 'green.200',
      },
      _focus: {
        borderColor: 'brand',
        boxShadow: '0 0 0 2px #5bce4c',
      },
    },
    [LayerStyles.INPUT]: {
      outline: 'none',
      height: 85,
      paddingRight: 65,
      fontSize: 22,
      border: '2px solid transparent',
      borderColor: 'grey',
      borderRadius: 'md',
    },
    [LayerStyles.INPUT_GROUP]: {
      borderRadius: 'md',
      border: '2px solid transparent',
      borderColor: 'green.200',
    },
    error: {
      fontWeight: 'bold',
      fontSize: 'large',
      textAlign: 'center',
      color: 'error',
    },
  },
  styles: {
    global: (props) => ({
      body: {
        bgColor: mode('white', 'black')(props),
        color: mode('black', 'white')(props),
        display: 'flex',
        minHeight: '100vh',
        flexDirection: 'column',
        margin: 0,
        textRendering: 'optimizeLegibility',
        fontSmooth: 'antialiased',
        WebkitFontSmoothing: 'antialiased',
        WebkitTapHighlightColor: 'transparent',
        whiteSpace: 'break-spaces',
      },
      '#root': {
        flex: 1,
      },
      input: {
        outline: 'none',
      },
      '.pac-target-input': {
        backgroundColor: 'transparent',
        '::placeholder': {
          color: 'ashGrey',
        },
      },
      '#currencyInput': {
        backgroundColor: 'transparent',
      },
      '.disabled': {
        pointerEvents: 'none',
        color: mode('grey', 'darkGrey')(props),
        cursor: 'none',
      },
      '.disabled div': {
        borderColor: mode('grey', 'darkGrey')(props),
      },
    }),
  },
  config: {
    initialColorMode: 'light',
    useSystemColorMode: true,
  },
})
